import { UserManager, WebStorageStateStore, User, UserManagerSettings, Log } from 'oidc-client';
import { BaseOidcAuthAdapter, BaseAuthConfig } from '../base/BaseOidcAuthAdapter';
import {
  AuthenticatedUser,
  AuthenticationErrorCode,
  LoginOptions,
  LogoutOptions
} from '../../auth/models/AuthenticationModels';

export interface ForgeGcpAuthConfig extends BaseAuthConfig {
  authority: string;
}

export class ForgeGcpAuthAdapter extends BaseOidcAuthAdapter<ForgeGcpAuthConfig> {
  
  protected createUserManager(config: ForgeGcpAuthConfig): UserManager {
    const settings: UserManagerSettings = {
      automaticSilentRenew: true,
      silent_redirect_uri: config.silentRedirectUri || `${window.location.origin}/silent-renew.html`,
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      post_logout_redirect_uri: config.postLogoutRedirectUri || window.location.origin,
      response_type: 'code', // Forge IAM uses Authorization Code flow
      scope: config.scopes.join(' '),
      filterProtocolClaims: true,
      loadUserInfo: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    };
    
    return new UserManager(settings);
  }
  
  protected transformToDomainUser(oidcUser: User): AuthenticatedUser {
    const profile = oidcUser.profile;
    return {
      id: profile.sub,
      email: profile.email || profile.preferred_username || '',
      name: profile.name || profile.email || '',
      givenName: profile.given_name,
      familyName: profile.family_name,
      roles: (profile.roles as any) || [],
      permissions: [],
      tenantId: (profile.tid as string) || '',
      organizationId: (profile.tid as string) || undefined,
      
      authenticatedAt: new Date((profile.auth_time || Date.now() / 1000) * 1000).toISOString(),
      expiresAt: new Date((oidcUser.expires_at || ((Date.now() / 1000) + 3600)) * 1000).toISOString(),
      identityProvider: 'forge-gcp',
      
      metadata: {
          idToken: oidcUser.id_token
      }
    };
  }

  protected getProviderErrorMap(): Record<string, AuthenticationErrorCode> {
    return {
      access_denied: AuthenticationErrorCode.USER_CANCELLED
    };
  }

  protected async performSignIn(options?: LoginOptions): Promise<void> {
     await this.userManager.signinRedirect({
       login_hint: options?.suggestedUser,
       prompt: options?.prompt,
       state: options?.redirectUrl ? btoa(JSON.stringify({ redirectUrl: options.redirectUrl })) : undefined
     });
  }

  protected async performSignOut(options?: LogoutOptions): Promise<void> {
     const user = await this.userManager.getUser();
     const idToken = user?.id_token;
     await this.userManager.removeUser();
     
     await this.userManager.signoutRedirect({
       id_token_hint: idToken,
       post_logout_redirect_uri: options?.redirectUrl || this.config.postLogoutRedirectUri,
     });
  }
}
